import type { SurveyScoresByCategory, SurveyScoresByQuestion } from '@engagement-web-app/common';
import classNames from 'classnames';
import { TrendingDown } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { useRanking } from '@/routes/system/_auth/surveys/-hooks';
import { formatNumber } from '@/utils';
import * as styles from './ImprovementArea.css';

/** Number of the worst-scoring categories to show, one card each. */
const CARD_COUNT = 4;

export type ImprovementAreaProps = {
    /** `null` while still loading. */
    scoresByCategory: SurveyScoresByCategory['categories'] | null;
    /** `null` while still loading. */
    scoresByQuestion: SurveyScoresByQuestion['questions'] | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
    /**
     * Wraps each question pill in a link, receiving the question's id and the pill to
     * place inside the link. Omit to render the pills as plain labels.
     */
    renderQuestionLink?: (questionId: string, pill: ReactNode) => ReactNode;
};

export function ImprovementArea({
    scoresByCategory,
    scoresByQuestion,
    isError,
    onRetry,
    renderQuestionLink,
}: ImprovementAreaProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="改善対象のカテゴリーの取得に失敗しました。" />;

    if (!scoresByCategory || !scoresByQuestion) return <ImprovementAreaSkeleton />;

    return (
        <ImprovementAreaContent
            scoresByCategory={scoresByCategory}
            scoresByQuestion={scoresByQuestion}
            renderQuestionLink={renderQuestionLink}
        />
    );
}

type ImprovementAreaContentProps = {
    [K in keyof Pick<ImprovementAreaProps, 'scoresByCategory' | 'scoresByQuestion'>]: NonNullable<
        ImprovementAreaProps[K]
    >;
} & Pick<ImprovementAreaProps, 'renderQuestionLink'>;

// Split from the buffer above so its hooks are never called conditionally.
function ImprovementAreaContent({
    scoresByCategory: categoryScores,
    scoresByQuestion: questionScores,
    renderQuestionLink,
}: ImprovementAreaContentProps) {
    const { worst: worstCategories } = useRanking('di', categoryScores, { limit: CARD_COUNT });
    const { worst: worstQuestions } = useRanking('di', questionScores, { limit: Infinity });

    const worstQuestionsByCategory = useMemo(() => {
        if (!worstQuestions) return new Map<string, SurveyScoresByQuestion['questions']>();

        const questionsByCategoryId = new Map<string, SurveyScoresByQuestion['questions']>();
        for (const question of worstQuestions) {
            if (!questionsByCategoryId.has(question.categoryId)) {
                questionsByCategoryId.set(question.categoryId, []);
            }
            questionsByCategoryId.get(question.categoryId)?.push(question);
        }

        return questionsByCategoryId;
    }, [worstQuestions]);

    return (
        <Section>
            <SectionHeader
                title="改善対象のカテゴリー"
                description="DI 平均が低いカテゴリーを最大 4 件抽出し、それぞれで否定的な回答が多かった設問を併せて表示します。改善施策をどこから始めるかの起点として使います。"
            />

            <Flex gap="6">
                {worstCategories.map((category) => (
                    <Flex key={category.id} direction="column" align="center" gap="4" flex className={styles.card}>
                        <div className={styles.iconCircle}>
                            <Icon icon={TrendingDown} size="lg" className={styles.icon} />
                        </div>

                        <div className={styles.cardTitle}>{category.name}</div>

                        <Flex gap="2" align="center">
                            <div className={styles.cardValue}>{category.di ? formatNumber(category.di) : 'N/A'}</div>
                            <div className={styles.valueUnit}>DI 平均</div>
                        </Flex>

                        <div className={styles.questions}>否定値高い​回答設問：</div>

                        <Flex as="ol" gap="2" wrap="wrap">
                            {worstQuestionsByCategory
                                .get(category.id)
                                ?.slice(0, 3)
                                .map((q) => {
                                    const pill = (
                                        <Pill
                                            variant="quinary"
                                            weight="bold"
                                            className={classNames({ [styles.linkedPill]: renderQuestionLink })}
                                        >
                                            {q.name}
                                        </Pill>
                                    );

                                    return (
                                        <li key={q.id} title={`DI: ${q.di}`}>
                                            {renderQuestionLink ? renderQuestionLink(q.id, pill) : pill}
                                        </li>
                                    );
                                })}
                        </Flex>
                    </Flex>
                ))}
            </Flex>
        </Section>
    );
}

export function ImprovementAreaSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6">
                <Flex direction="column" gap="1">
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Flex gap="6">
                    {Array.from({ length: CARD_COUNT }).map((_, idx) => (
                        <Flex
                            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder, the cards have no identity
                            key={idx}
                            direction="column"
                            align="center"
                            gap="4"
                            flex
                            className={styles.card}
                        >
                            <Skeleton.Circle radius={56} />
                            <Skeleton.Line width="70%" height="2xl" />
                            <Skeleton.Line width="40%" height="4xl" />
                            <Skeleton.Line width="60%" height="xs" />

                            <Flex gap="2" wrap="wrap" justify="center" fullWidth>
                                <Skeleton.Box width="40%" height={28} />
                                <Skeleton.Box width="30%" height={28} />
                            </Flex>
                        </Flex>
                    ))}
                </Flex>
            </Flex>
        </Section>
    );
}
